import React, { useState } from "react";
import { Card, Spin, Row, Col, Statistic, Divider, Button, Modal, DatePicker, Space } from "antd";
import { LoadingOutlined, DownOutlined, UpOutlined, FileExcelOutlined } from "@ant-design/icons";
import CountUp from "react-countup";
import { useNavigate } from "react-router-dom";
import { downloadTurnoverDetailsExcel } from "../../api/api";
import dayjs, { Dayjs } from "dayjs";
import { useAuth } from "../../contexts/auth.context";
import { hasAdminAccess } from "../../utils/roleUtils";

interface TurnoverProps {
  data: {
    turnover: number;
    totalProduceUSD: number;
    totalMaterialsUSD: number;
    totalDebtUSD: number;
    totalCompanyDebtUSD: number;
    totalClientPrepaidUSD: number;
    totalCompanyPrepaidUSD: number;
    totalCashboxBalanceUSD: number;
    totalCashTransferBalanceUSD: number;
    clientStatsByType: {
      [key: string]: {
        totalDebtUSD: number;
        totalCompanyDebtUSD: number;
        totalClientPrepaidAmountUSD: number;
        totalCompanyPrepaidAmountUSD: number;
        count: number;
      };
    };
  } | null;
  loading: boolean;
}

export default function Turnover({ data, loading }: TurnoverProps) {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const isAdmin = hasAdminAccess(authState.user?.role);
  const [showDetails, setShowDetails] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comparisonDate, setComparisonDate] = useState<Dayjs | null>(null);
  
  const showModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setComparisonDate(null);
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    setIsModalOpen(false);
    try {
      const comparisonDateStr = comparisonDate ? comparisonDate.format("DD.MM.YYYY") : undefined;
      await downloadTurnoverDetailsExcel(comparisonDateStr);
      setComparisonDate(null);
    } catch (error) {
      console.error("Error downloading Excel:", error);
    } finally {
      setDownloadingExcel(false);
    }
  };
  
  if (loading) {
    return (
      <Card bordered={false} style={{ width: "100%" }}>
        <Spin indicator={<LoadingOutlined spin />} />
      </Card>
    );
  }

  if (!data) {
    return (
      <Card bordered={false} style={{ width: "100%" }}>
        <div>No data available</div>
      </Card>
    );
  }

  return (
    <>
    <Card 
      bordered={false} 
      style={{ width: "100%" }} 
      onClick={() => navigate("/turnover/logs")} 
      hoverable
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Общий оборот</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {isAdmin && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/turnover/excel-reports");
                }}
              >
                Архив Excel
              </Button>
            )}
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={showModal}
              loading={downloadingExcel}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >
              Скачать детальный Excel
            </Button>
            <CountUp
              start={0}
              end={data.turnover}
              duration={2.5}
              decimals={2}
              prefix="$"
              separator=" "
              style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}
            />
          </div>
        </div>
      }
    >
      {/* Общая разбивка цифр оборота */}
      {showDetails && (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Statistic
                title="Продукция"
                value={data.totalProduceUSD}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Statistic
                title="Материалы"
                value={data.totalMaterialsUSD}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Statistic
                title="Долг клиентов"
                value={data.totalDebtUSD}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Statistic
                title="Долг компании"
                value={data.totalCompanyDebtUSD}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Statistic
                title="Баланс касс"
                value={data.totalCashboxBalanceUSD}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Statistic
                title="Конвертация"
                value={data.totalCashTransferBalanceUSD}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
          </Row>
          
          <Divider />
        </>
      )}
      
      {/* Кнопка для показа детальной разбивки */}
      {data.clientStatsByType && Object.keys(data.clientStatsByType).length > 0 && (
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Button 
            type="link" 
            onClick={(e) => {
              e.stopPropagation(); // Предотвращаем переход на другую страницу
              setShowDetails(!showDetails);
            }}
            icon={showDetails ? <UpOutlined /> : <DownOutlined />}
            style={{ color: '#1890ff' }}
          >
            {showDetails ? 'Скрыть детали' : 'Показать подробную разбивку оборота'}
          </Button>
        </div>
      )}
      
      {/* Детальная разбивка по типам клиентов */}
      {showDetails && data.clientStatsByType && Object.keys(data.clientStatsByType).length > 0 && (
        <>
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 16px 0', color: '#1890ff' }}>Детальная разбивка по типам клиентов:</h4>
            <Row gutter={[16, 16]}>
              {Object.entries(data.clientStatsByType).map(([clientType, stats]) => (
                <Col xs={24} sm={12} md={8} key={clientType}>
                  <Card 
                    size="small" 
                    title={
                      <span style={{ 
                        color: clientType === 'purchase' ? '#52c41a' : 
                               clientType === 'sale' ? '#1890ff' : '#fa8c16',
                        fontWeight: 'bold'
                      }}>
                        {clientType === 'purchase' ? 'Покупки' : 
                         clientType === 'sale' ? 'Продажи' : 
                         clientType === 'scrap' ? 'Лом' : clientType}
                        <span style={{ fontSize: '12px', marginLeft: '8px', color: '#666' }}>
                          ({stats.count} клиентов)
                        </span>
                      </span>
                    }
                    style={{ marginBottom: 8 }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span>Долг клиентов:</span>
                      <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                        ${stats.totalDebtUSD}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span>Долг компании:</span>
                      <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                        ${stats.totalCompanyDebtUSD}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span>Предоплата клиентов:</span>
                      <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                        ${stats.totalClientPrepaidAmountUSD}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Предоплата компании:</span>
                      <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                        ${stats.totalCompanyPrepaidAmountUSD}
                      </span>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
          <Divider />
        </>
      )}
      
      <div style={{ textAlign: 'center', marginTop: 16 }}>
        <small style={{ color: '#666' }}>
          Формула: Продукция + Материалы + Долг клиентов - Долг компании - Предоплата клиентов + Предоплата компании + Баланс касс + Конвертация
        </small>
      </div>
    </Card>

    <Modal
      title="Экспорт детального отчета в Excel"
      open={isModalOpen}
      onOk={handleDownloadExcel}
      onCancel={handleCancel}
      okText="Скачать"
      cancelText="Отмена"
      okButtonProps={{ 
        loading: downloadingExcel,
        icon: <FileExcelOutlined />,
        style: { backgroundColor: '#52c41a', borderColor: '#52c41a' }
      }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <p style={{ marginBottom: 8, fontWeight: 500 }}>
            Выберите дату для сравнения оборота (опционально):
          </p>
          <DatePicker
            value={comparisonDate}
            onChange={(date) => setComparisonDate(date)}
            format="DD.MM.YYYY"
            placeholder="Выберите дату сравнения"
            style={{ width: '100%' }}
            disabledDate={(current) => {
              // Запрещаем выбирать будущие даты
              return current && current > dayjs().endOf('day');
            }}
          />
          <small style={{ color: '#666', marginTop: 8, display: 'block' }}>
            Если дата не выбрана, сравнение будет с оборотом месяц назад
          </small>
        </div>
        
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#f0f5ff', 
          borderRadius: '4px',
          border: '1px solid #adc6ff'
        }}>
          <p style={{ margin: 0, fontSize: '12px', color: '#1890ff' }}>
            <strong>Что будет в отчете:</strong>
          </p>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '12px' }}>
            <li>Детальная разбивка по продукции, материалам, клиентам</li>
            <li>Сравнение с выбранной датой</li>
            <li>Учет инвестиций и выводов за период</li>
            <li>Расчет итоговой прибыли</li>
          </ul>
        </div>
      </Space>
    </Modal>
    </>
  );
}
